import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { PhysicalButton } from "@/components/ui/physical-button";
import { StatusLight } from "@/components/ui/status-light";

export const Route = createFileRoute("/_layout/test-media-session")({
  component: TestMediaSession,
});

function TestMediaSession() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);

  const addLog = (message: string) => {
    const timestamp = new Date().toISOString().split("T")[1].split(".")[0];
    setLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
  };

  useEffect(() => {
    const supported = "mediaSession" in navigator;
    setIsSupported(supported);
    addLog(`MediaSession API supported: ${supported}`);

    if (supported) {
      addLog(`Browser: ${navigator.userAgent}`);

      // Test available actions
      const testActions = ["play", "pause", "previoustrack", "nexttrack"];
      const available: string[] = [];

      testActions.forEach((action) => {
        try {
          navigator.mediaSession.setActionHandler(action as any, null);
          available.push(action);
        } catch (_e) {
          // Not supported
        }
      });

      addLog(`Available actions: ${available.join(", ")}`);
    }
  }, [addLog]);

  const handlePlay = async () => {
    if (!audioRef.current) {
      return;
    }

    try {
      addLog("Attempting to play audio...");
      await audioRef.current.play();
      setIsPlaying(true);
      addLog("Audio playing successfully");

      // Set up MediaSession
      if ("mediaSession" in navigator) {
        addLog("Setting up MediaSession metadata...");

        try {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: "Test Track",
            artist: "Test Artist",
            album: "Test Album",
            artwork: [
              {
                src: "https://via.placeholder.com/96",
                sizes: "96x96",
                type: "image/png",
              },
              {
                src: "https://via.placeholder.com/512",
                sizes: "512x512",
                type: "image/png",
              },
            ],
          });

          addLog("Metadata set successfully");
          addLog(
            `Current metadata: ${JSON.stringify({
              title: navigator.mediaSession.metadata?.title,
              artist: navigator.mediaSession.metadata?.artist,
              album: navigator.mediaSession.metadata?.album,
            })}`
          );

          // Set playback state
          navigator.mediaSession.playbackState = "playing";
          addLog(`Playback state: ${navigator.mediaSession.playbackState}`);

          // Set up action handlers
          navigator.mediaSession.setActionHandler("play", () => {
            addLog("Play action triggered from MediaSession");
            audioRef.current?.play();
            setIsPlaying(true);
          });

          navigator.mediaSession.setActionHandler("pause", () => {
            addLog("Pause action triggered from MediaSession");
            audioRef.current?.pause();
            setIsPlaying(false);
          });

          navigator.mediaSession.setActionHandler("previoustrack", () => {
            addLog("Previous track action triggered");
          });

          navigator.mediaSession.setActionHandler("nexttrack", () => {
            addLog("Next track action triggered");
          });

          addLog("Action handlers set up successfully");
        } catch (error) {
          addLog(`Error setting up MediaSession: ${error}`);
        }
      }
    } catch (error) {
      addLog(`Error playing audio: ${error}`);
    }
  };

  const handlePause = () => {
    if (!audioRef.current) {
      return;
    }

    audioRef.current.pause();
    setIsPlaying(false);
    addLog("Audio paused");

    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = "paused";
      addLog(`Playback state: ${navigator.mediaSession.playbackState}`);
    }
  };

  const updateMetadata = () => {
    if (!isSupported) {
      return;
    }

    addLog("Updating metadata with new values...");

    try {
      const newTitle = `Updated Track ${Date.now()}`;
      navigator.mediaSession.metadata = new MediaMetadata({
        title: newTitle,
        artist: "Updated Artist",
        album: "Updated Album",
      });

      addLog(`Metadata updated: ${newTitle}`);
    } catch (error) {
      addLog(`Error updating metadata: ${error}`);
    }
  };

  const clearLogs = () => {
    setLogs([]);
    addLog("Logs cleared");
  };

  return (
    <div className="container mx-auto max-w-4xl p-6">
      <h1 className="mb-6 font-bold text-2xl">MediaSession API Test</h1>

      <div className="space-y-6">
        {/* Status */}
        <div className="flex items-center gap-4">
          <StatusLight status={isSupported ? "active" : "inactive"} />
          <span>
            MediaSession API {isSupported ? "Supported" : "Not Supported"}
          </span>
        </div>

        {/* Audio Element */}
        <div className="rounded bg-gray-100 p-4">
          <p className="mb-2 text-gray-600 text-sm">
            Audio Element (required for MediaSession)
          </p>
          <audio
            onError={(e) =>
              addLog(`Audio element error: ${e.currentTarget.error?.message}`)
            }
            onLoadedMetadata={() => addLog("Audio element: metadata loaded")}
            onPause={() => addLog("Audio element: pause event")}
            onPlay={() => addLog("Audio element: play event")}
            ref={audioRef}
            src="https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3"
          />
        </div>

        {/* Controls */}
        <div className="flex gap-4">
          <PhysicalButton
            disabled={isPlaying}
            onClick={handlePlay}
            variant="primary"
          >
            Play
          </PhysicalButton>

          <PhysicalButton
            disabled={!isPlaying}
            onClick={handlePause}
            variant="secondary"
          >
            Pause
          </PhysicalButton>

          <PhysicalButton
            disabled={!isSupported}
            onClick={updateMetadata}
            variant="secondary"
          >
            Update Metadata
          </PhysicalButton>

          <PhysicalButton onClick={clearLogs} variant="danger">
            Clear Logs
          </PhysicalButton>
        </div>

        {/* Instructions */}
        <div className="rounded bg-blue-50 p-4">
          <h3 className="mb-2 font-semibold">Testing Instructions:</h3>
          <ol className="list-inside list-decimal space-y-1 text-sm">
            <li>Click "Play" to start audio and set up MediaSession</li>
            <li>
              Check your browser's media controls (keyboard media keys,
              notification area)
            </li>
            <li>Try using media controls to play/pause</li>
            <li>Check browser console for detailed logs</li>
            <li>On mobile: Check lock screen controls</li>
          </ol>
        </div>

        {/* Logs */}
        <div className="rounded border p-4">
          <h3 className="mb-2 font-semibold">Debug Logs:</h3>
          <div className="max-h-96 space-y-1 overflow-y-auto font-mono text-xs">
            {logs.length === 0 ? (
              <p className="text-gray-500">No logs yet...</p>
            ) : (
              logs.map((log, i) => (
                <div className="text-gray-700" key={i}>
                  {log}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
